import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { Monogram } from '@/components/editorial/Monogram';
import { Hairline, Kicker, Ornament } from '@/components/editorial/Ornament';
import { COMPANY } from '@/i18n/company';

/*
  Couture 마스트헤드 레이아웃
  · 상단: 얇은 골드 룰 + 좌측 ISSUE 표기 + 중앙 모노그램·워드마크 + 우측 계정
  · 네비게이션은 매거진 목차 톤 — 라벨 사이 큰 자간, 호버 시 골드 룰
*/

// 메뉴: 한글 라벨이 메인, 영문은 작은 키커로 보조
const NAV_ITEMS = [
  { to: '/', label: '홈', en: 'Maison', exact: true },
  { to: '/templates', label: '템플릿', en: 'Atelier' },
];

export function RootLayout() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const location = useLocation();

  return (
    <div className="flex min-h-screen flex-col">
      {/* 상단 트림 — 발행 호수 표기 */}
      <div className="border-b border-ink/10">
        <div className="container flex items-center justify-between py-2 text-[10px] uppercase text-ink/60" style={{ letterSpacing: '0.32em' }}>
          <span>Vol. I — Spring · Summer 2026</span>
          <span className="hidden md:inline">Édition Numérique · 디지털 한정</span>
          <span>Made in Seoul</span>
        </div>
      </div>

      {/* 마스트헤드 */}
      <header className="border-b border-ink/15 bg-ivory/70 backdrop-blur-xl">
        <div className="container">
          {/* 좌: 네비 / 중: 모노그램+워드마크 / 우: 계정 */}
          <div className="grid grid-cols-3 items-center py-7">
            <nav className="hidden items-center gap-8 md:flex">
              {NAV_ITEMS.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </nav>

            <Link
              to="/"
              className="flex flex-col items-center gap-1 transition-opacity hover:opacity-80"
            >
              <Monogram className="text-gold" size={36} />
              <span
                className="font-display text-2xl tracking-[0.18em] md:text-[26px]"
                style={{ fontStyle: 'italic' }}
              >
                TilOurDay
              </span>
              <Hairline gold className="w-12" />
            </Link>

            <div className="flex items-center justify-end gap-6">
              {status === 'signed-in' ? (
                <div className="flex items-center gap-3">
                  <Link
                    to="/my"
                    className="text-[12.5px] text-ink/75 hover:text-gold"
                  >
                    내 청첩장
                    {user?.displayName && <span className="ml-1.5 text-ink/40">· {firstName(user.displayName)}</span>}
                  </Link>
                  <span className="hidden h-3 w-px bg-ink/20 md:inline-block" />
                  <button
                    onClick={signOut}
                    className="hidden text-[12.5px] text-ink/55 hover:text-gold md:inline"
                  >
                    로그아웃
                  </button>
                </div>
              ) : (
                <Link
                  to={`/login?next=${encodeURIComponent(location.pathname)}`}
                  className="text-[12.5px] text-ink hover:text-gold"
                >
                  운영자 로그인 →
                </Link>
              )}
            </div>
          </div>

          {/* 모바일 네비 */}
          <nav className="flex items-center justify-center gap-6 border-t border-ink/10 py-3 md:hidden">
            {NAV_ITEMS.map((item) => (
              <NavItem key={item.to} {...item} mobile />
            ))}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* 푸터 — 매거진 콜로폰 */}
      <footer className="mt-24 border-t border-ink/15">
        <div className="container py-16">
          <Ornament className="mb-12" />
          <div className="grid gap-12 md:grid-cols-[1.6fr,1fr,1fr]">
            <div>
              <Kicker>The Maison</Kicker>
              <h3 className="mt-4 font-display text-3xl italic">TilOurDay</h3>
              <p className="mt-3 max-w-xs text-sm text-ink/70">
                결혼식 당일까지만 빛나는 3D 청첩장.
                평생 한 번뿐인 그날을 디지털 한정판으로 새깁니다.
              </p>
            </div>
            <FooterCol
              title="Atelier"
              links={[{ to: '/templates', label: '템플릿 8종' }]}
            />
            <FooterCol
              title="Contact"
              external={[{ href: `mailto:${COMPANY.email}`, label: COMPANY.email }]}
              links={[{ to: '/privacy', label: '개인정보 처리방침' }]}
            />
          </div>

          <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-ink/10 pt-6 text-[10px] uppercase text-ink/50 md:flex-row" style={{ letterSpacing: '0.28em' }}>
            <span>© {new Date().getFullYear()} TilOurDay · {COMPANY.name}</span>
            <span>A digital atelier for the unrepeatable day.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, label, en, exact, mobile }: { to: string; label: string; en?: string; exact?: boolean; mobile?: boolean }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        cn(
          'group relative inline-flex flex-col items-center text-[13.5px] leading-tight transition-colors',
          isActive ? 'text-ink' : 'text-ink/65 hover:text-ink',
          mobile && 'text-[12.5px]',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span>{label}</span>
          {en && (
            <span
              className="mt-0.5 text-[8.5px] uppercase text-gold/60"
              style={{ letterSpacing: '0.32em' }}
            >
              {en}
            </span>
          )}
          <span
            className={cn(
              'absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-500',
              isActive ? 'w-full' : 'w-0 group-hover:w-full',
            )}
          />
        </>
      )}
    </NavLink>
  );
}

function FooterCol({
  title,
  links,
  external,
}: {
  title: string;
  links?: { to: string; label: string }[];
  external?: { href: string; label: string }[];
}) {
  return (
    <div>
      <Kicker withBar={false}>{title}</Kicker>
      <ul className="mt-4 space-y-2.5">
        {links?.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-ink/70 transition-colors hover:text-gold">
              {l.label}
            </Link>
          </li>
        ))}
        {external?.map((l) => (
          <li key={l.href}>
            <a href={l.href} className="text-sm text-ink/70 transition-colors hover:text-gold">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function firstName(displayName?: string | null) {
  if (!displayName) return null;
  return displayName.split(' ')[0];
}
